import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ZapIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { TimeSlot } from '@hero/hooks-shared';

/**
 * Bucket a slot's start hour into a daypart label. Matches how the
 * customer-app `DatePickerModal` groups its hour chips, so the two
 * surfaces feel like the same product.
 *
 *  Morning   · hour < 12  (07:00, 09:00, 11:00)
 *  Afternoon · 12 ≤ h < 17 (13:00, 15:00)
 *  Evening   · 17 ≤ h < 21 (17:00, 19:00, 20:00)
 *  Night     · h ≥ 21
 */
export type TimeSlotPeriod = 'morning' | 'afternoon' | 'evening' | 'night';

const PERIOD_ORDER: TimeSlotPeriod[] = ['morning', 'afternoon', 'evening', 'night'];

function getPeriod(hour: number): TimeSlotPeriod {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
}

export interface TimeSlotChunksProps {
  /**
   * Slots produced by `useOrderTimeSlots` (or anything matching the
   * `TimeSlot` shape). Order within each daypart is preserved; only the
   * grouping order is enforced.
   */
  slots: TimeSlot[];
  /** Currently selected slot's `value`. */
  value?: string | null;
  /** Called with the slot's `value` when the user picks a chip. */
  onChange: (slotValue: string) => void;
  className?: string;
  /** Override the empty message; defaults to the `c-dates` string. */
  emptyMessage?: string;
}

/**
 * Renders pickup time slots grouped into daypart sections (Morning /
 * Afternoon / Evening / Night), each with a row of chip buttons. Built
 * to be reused anywhere a "pick a time slot" UI is needed — currently
 * the carrier `PickupDateDrawer`, and a natural fit for the customer
 * dashboard once it migrates off Redux.
 */
export function TimeSlotChunks({
  slots,
  value,
  onChange,
  className,
  emptyMessage,
}: TimeSlotChunksProps) {
  const { t } = useTranslation('c-dates');

  const groups = useMemo(() => {
    const map = new Map<TimeSlotPeriod, TimeSlot[]>();
    for (const slot of slots) {
      const period = getPeriod(slot.from.hour());
      if (!map.has(period)) map.set(period, []);
      map.get(period)!.push(slot);
    }
    return PERIOD_ORDER.filter(p => map.has(p)).map(p => ({
      period: p,
      items: map.get(p) as TimeSlot[],
    }));
  }, [slots]);


  if (slots.length === 0) {
    return (
      <div className={cn('py-6 text-center text-sm text-gray-500', className)}>
        {emptyMessage ?? t('time_periods.empty', { defaultValue: 'No available slots' })}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {groups.map(({ period, items }) => (
        <div key={period}>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
            {t(`time_periods.${period}`, { defaultValue: period })}
          </div>
          <div className="flex flex-wrap gap-2">
            {items.map(slot => {
              const isSelected = slot.value === value;
              const isNow = slot.isNow;
              return (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() => onChange(slot.value)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                    'flex items-center gap-1.5',
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
                  )}>
                  {isNow && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        isSelected
                          ? 'bg-white/25 text-white'
                          : 'bg-amber-100 text-amber-700',
                      )}>
                      <ZapIcon className="size-3" />
                      {t('now', { defaultValue: 'Now' })}
                    </span>
                  )}
                  <span>{slot.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
